import type {
  AdminFile,
  AdminFileFolder,
  AdminFileFolderListResponse,
  AdminFileFolderResponse,
  AdminFileListResponse,
  AdminFileResponse,
  BulkFileResult,
  BulkFileResultResponse,
  FileImageWidth,
  FileMimeType,
  FileUploadStatus,
  FileVisibility,
  InitiatedFileUpload,
  InitiatedFileUploadResponse,
  PresignedFileDownload,
  PresignedFileDownloadResponse,
  PresignedFileUpload,
  PresignedFileUploadResponse,
} from '@mds/shared/types/file';

import { api } from '@/lib/api';

/** The file endpoints — see `backend/docs/file-storage.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type FileQuery = {
  page?: number;
  limit?: number;
  q?: string;
  mimeType?: FileMimeType;
  visibility?: FileVisibility;
  uploadStatus?: FileUploadStatus;
  /** A folder's ID, or `root` for files in no folder. Omitted searches every folder. */
  folderId?: string;
  includeDeleted?: boolean;
  sort?: 'createdAt' | 'originalFilename' | 'sizeBytes' | 'uploadStatus';
  direction?: 'asc' | 'desc';
};

export const listFiles = (query: FileQuery = {}) =>
  api.get<AdminFileListResponse>('admin/files', { query });

export const getFile = (fileId: string): Promise<AdminFile> =>
  api.get<AdminFileResponse>(`admin/files/${fileId}`).then((r) => r.data);

export interface InitiateUploadBody {
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  visibility: FileVisibility;
  /** Null puts it at the root. */
  folderId: string | null;
}

/** Step one: reserves a pending row and returns the exact upload contract. */
export const initiateUpload = (body: InitiateUploadBody): Promise<InitiatedFileUpload> =>
  api.post<InitiatedFileUploadResponse>('admin/files/uploads', body).then((r) => r.data);

/** Step three: the API checks type, length and checksum before marking it ready. */
export const completeUpload = (fileId: string): Promise<AdminFile> =>
  api.post<AdminFileResponse>(`admin/files/${fileId}/complete`, {}).then((r) => r.data);

/** Rename, switch visibility, or move — only the fields given change. */
export interface FileChanges {
  originalFilename?: string;
  visibility?: FileVisibility;
  folderId?: string | null;
}

export const updateFile = (fileId: string, changes: FileChanges): Promise<AdminFile> =>
  api.patch<AdminFileResponse>(`admin/files/${fileId}`, changes).then((r) => r.data);

/** Up to 100 at a time. Files that cannot be deleted come back in `failed`; the rest still go. */
export const bulkDelete = (fileIds: string[]): Promise<BulkFileResult> =>
  api.post<BulkFileResultResponse>('admin/files/bulk-delete', { fileIds }).then((r) => r.data);

export const bulkMove = (fileIds: string[], folderId: string | null): Promise<BulkFileResult> =>
  api
    .post<BulkFileResultResponse>('admin/files/bulk-move', { fileIds, folderId })
    .then((r) => r.data);

/** A preview upload: the same exact PUT contract as a file, for the file's one preview slot. */
export const initiateThumbnail = (
  fileId: string,
  body: { sizeBytes: number; checksumSha256: string },
): Promise<PresignedFileUpload> =>
  api
    .post<PresignedFileUploadResponse>(`admin/files/${fileId}/thumbnail`, body)
    .then((r) => r.data);

export const completeThumbnail = (fileId: string): Promise<AdminFile> =>
  api.post<AdminFileResponse>(`admin/files/${fileId}/thumbnail/complete`, {}).then((r) => r.data);

/** A display size: the same exact PUT contract again, for the slot of one width. */
export const initiateVariant = (
  fileId: string,
  body: { width: FileImageWidth; sizeBytes: number; checksumSha256: string },
): Promise<PresignedFileUpload> =>
  api.post<PresignedFileUploadResponse>(`admin/files/${fileId}/variants`, body).then((r) => r.data);

export const completeVariant = (fileId: string, width: FileImageWidth): Promise<AdminFile> =>
  api
    .post<AdminFileResponse>(`admin/files/${fileId}/variants/complete`, { width })
    .then((r) => r.data);

/** The file's bytes, relayed by the API — for making a preview of a file that has none. */
export const fetchContent = (fileId: string): Promise<Blob> =>
  api.getBlob(`admin/files/${fileId}/content`).then((r) => r.blob);

export const listFolders = (): Promise<AdminFileFolder[]> =>
  api.get<AdminFileFolderListResponse>('admin/files/folders').then((r) => r.data);

export const createFolder = (name: string, parentId: string | null): Promise<AdminFileFolder> =>
  api.post<AdminFileFolderResponse>('admin/files/folders', { name, parentId }).then((r) => r.data);

export const updateFolder = (
  folderId: string,
  changes: { name?: string; parentId?: string | null },
): Promise<AdminFileFolder> =>
  api
    .patch<AdminFileFolderResponse>(`admin/files/folders/${folderId}`, changes)
    .then((r) => r.data);

/** Only an empty folder can be deleted; anything else is a 409 `FOLDER_NOT_EMPTY`. */
export const deleteFolder = (folderId: string): Promise<void> =>
  api.delete<null>(`admin/files/folders/${folderId}`).then(() => undefined);

/** Short-lived, single-use in practice. Fetch one per download rather than caching. */
export const downloadUrl = (fileId: string): Promise<PresignedFileDownload> =>
  api.get<PresignedFileDownloadResponse>(`admin/files/${fileId}/download-url`).then((r) => r.data);

/**
 * Soft delete: blocks access immediately, keeps the object for restore.
 *
 * The API answers 204 with no body, so there is nothing to unwrap — reading
 * `.data` off the null it resolves to is what used to report a delete that
 * had in fact succeeded as "That did not work".
 */
export const softDelete = (fileId: string): Promise<void> =>
  api.delete<null>(`admin/files/${fileId}`).then(() => undefined);

export const restore = (fileId: string): Promise<AdminFile> =>
  api.post<AdminFileResponse>(`admin/files/${fileId}/restore`, {}).then((r) => r.data);

/** Permanent, super_admin only, and only after a soft delete. */
export const purge = (fileId: string): Promise<null> =>
  api.delete<null>(`admin/files/${fileId}/purge`);
